import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader } from './ui/Card';
import { CardListToolbar } from './ui/CardListToolbar';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { AlertMessage } from './ui/AlertMessage';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { Modal } from './ui/Modal';
import { EmptyState } from './ui/EmptyState';
import { SkeletonRow } from './ui/Skeleton';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

function Brokers() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [editingItem, setEditingItem] = useState(null);
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [nameError, setNameError] = useState('');
  const [formError, setFormError] = useState('');
  const [pageMessage, setPageMessage] = useState('');
  const [pageError, setPageError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const headers = useMemo(() => {
    const token = localStorage.getItem('token');
    const h = { 'Content-Type': 'application/json' };
    if (token) {
      h.Authorization = `Bearer ${token}`;
    }
    return h;
  }, []);

  const fetchBrokers = async () => {
    setLoading(true);
    setPageError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/brokers`, { headers });
      if (!response.ok) {
        throw new Error('Unable to load brokers');
      }
      const data = await response.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setItems([]);
      setPageError(err?.message || 'Unable to load brokers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrokers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pageMessage) return undefined;
    const timer = setTimeout(() => setPageMessage(''), 3500);
    return () => clearTimeout(timer);
  }, [pageMessage]);

  useEffect(() => {
    if (!pageError) return undefined;
    const timer = setTimeout(() => setPageError(''), 5000);
    return () => clearTimeout(timer);
  }, [pageError]);

  useEffect(() => {
    if (!formError) return undefined;
    const timer = setTimeout(() => setFormError(''), 5000);
    return () => clearTimeout(timer);
  }, [formError]);

  const filtered = items.filter((b) => {
    const key = search.trim().toLowerCase();
    if (!key) return true;
    return (
      String(b.name || '').toLowerCase().includes(key) ||
      String(b.contact || '').toLowerCase().includes(key)
    );
  });

  const openCreate = () => {
    setFormMode('create');
    setEditingItem(null);
    setName('');
    setContact('');
    setNameError('');
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (row) => {
    setFormMode('edit');
    setEditingItem(row);
    setName(String(row.name || ''));
    setContact(String(row.contact || ''));
    setNameError('');
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormMode('create');
    setEditingItem(null);
    setName('');
    setContact('');
    setNameError('');
    setFormError('');
    setSaving(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPageMessage('');
    setPageError('');
    setFormError('');
    setNameError('');
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('Please enter name');
      return;
    }
    setSaving(true);
    try {
      const isCreate = formMode === 'create';
      const response = await fetch(
        isCreate ? `${API_BASE_URL}/api/brokers` : `${API_BASE_URL}/api/brokers/${editingItem.id}`,
        {
          method: isCreate ? 'POST' : 'PUT',
          headers,
          body: JSON.stringify({
            name: trimmedName,
            contact: contact.trim() || null,
          }),
        }
      );
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.error || errData?.message || 'Unable to save broker');
      }
      const saved = await response.json();
      setItems((prev) => (isCreate ? [saved, ...prev] : prev.map((x) => (x.id === saved.id ? saved : x))));
      setPageMessage(isCreate ? 'Broker created successfully' : 'Broker updated successfully');
      closeForm();
    } catch (err) {
      setFormError(err?.message || 'Unable to save broker');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteItem) return;
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/brokers/${confirmDeleteItem.id}`, {
        method: 'DELETE',
        headers,
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.error || errData?.message || 'Unable to delete broker');
      }
      setItems((prev) => prev.filter((x) => x.id !== confirmDeleteItem.id));
      setPageMessage('Broker deleted successfully');
      setConfirmDeleteItem(null);
    } catch (err) {
      setPageError(err?.message || 'Unable to delete broker');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div>
      <Card>
        <CardHeader variant="gradient">
          <CardListToolbar
            tone="light"
            title="Brokers"
            actions={(
              <>
                <div className="w-full sm:w-64">
                  <Input
                    type="text"
                    placeholder="Search brokers..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    leftIcon={(
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    )}
                  />
                </div>
                <Button
                  type="button"
                  variant="primary"
                  onClick={openCreate}
                  leftIcon={(
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  )}
                >
                  Add Broker
                </Button>
              </>
            )}
          />
        </CardHeader>
        <CardBody>
          <AlertMessage message={pageMessage} type="success" onClose={() => setPageMessage('')} className="mb-4" />
          <AlertMessage message={pageError} type="error" onClose={() => setPageError('')} className="mb-4" />

          {!loading && filtered.length === 0 ? (
            <EmptyState
              title={search ? 'No brokers match your search' : 'No brokers yet'}
              description={search ? 'Try a different search term.' : 'Add brokers you work with.'}
              action={!search ? <Button type="button" variant="primary" onClick={openCreate}>Add Broker</Button> : null}
            />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th className="w-14">#</th>
                    <th>Name</th>
                    <th>Contact</th>
                    <th className="text-right pr-6">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} columns={4} />)
                    : filtered.map((row, i) => (
                      <tr key={row.id}>
                        <td className="text-slate-400 tabular-nums">{i + 1}</td>
                        <td className="font-medium text-slate-800">{row.name || '—'}</td>
                        <td className="text-slate-700 text-sm max-w-[200px]">
                          <span className="block truncate" title={row.contact || ''}>{row.contact || '—'}</span>
                        </td>
                        <td className="pr-6">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => openEdit(row)}
                              className="icon-action icon-action-primary"
                              title="Edit"
                              aria-label={`Edit ${row.name}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmDeleteItem(row)}
                              className="icon-action icon-action-danger"
                              title="Delete"
                              aria-label={`Delete ${row.name}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        open={showForm}
        onClose={closeForm}
        size="md"
        title={formMode === 'create' ? 'Add Broker' : 'Edit Broker'}
        description="Name is required; add contact details as you prefer."
        footer={(
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                if (formMode === 'edit' && editingItem) {
                  setName(String(editingItem.name || ''));
                  setContact(String(editingItem.contact || ''));
                } else {
                  setName('');
                  setContact('');
                }
                setNameError('');
                setFormError('');
              }}
              disabled={saving}
            >
              Reset
            </Button>
            <Button type="submit" form="broker-form" variant="primary" loading={saving} disabled={saving}>
              {formMode === 'create' ? 'Create' : 'Update'}
            </Button>
          </>
        )}
      >
        <form id="broker-form" onSubmit={handleSubmit} className="py-6 space-y-4">
          <AlertMessage message={formError} type="error" onClose={() => setFormError('')} />
          <Input
            label="Name *"
            placeholder="Broker name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={nameError}
            required
          />
          <Input
            label="Contact"
            placeholder="Phone, WhatsApp, email…"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
          />
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirmDeleteItem)}
        title="Delete Broker"
        message={confirmDeleteItem ? `Delete broker "${confirmDeleteItem.name}"?` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteItem(null)}
        loading={deleteLoading}
        variant="danger"
      />
    </div>
  );
}

export default Brokers;
